import React, { useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

const FilterPill = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        {label}
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="200px"
          maxH="250px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                size="sm"
                colorScheme="limeShade"
                isChecked={selected.some((s) => s.value === option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected, option]);
                  } else {
                    onChange(selected.filter((s) => s.value !== option.value));
                  }
                }}
                py={1.5}
                w="100%"
              >
                <Text fontSize="13px" color="gray.600">
                  {option.label}
                </Text>
              </Checkbox>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

const DimensionFilterPill = ({ dimensionGroups, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        Dimension
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="220px"
          maxH="300px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {dimensionGroups.map((group, gIdx) => (
              <React.Fragment key={group.dimKey}>
                {gIdx > 0 && <Box w="100%" borderTop="1px solid" borderColor="gray.100" my={1} />}
                <Text fontSize="12px" fontWeight="700" color="gray.700" textTransform="uppercase" pt={gIdx > 0 ? 1 : 0} pb={1}>
                  {group.name}
                </Text>
                {group.items
                  .filter((item) => {
                    if (item.state !== "ARCHIVED") return true;
                    // Keep an archived item visible if it's already selected,
                    // so the user can deselect it.
                    return selected.some((s) => s.value === `${group.dimKey}:${item.id}`);
                  })
                  .map((item) => {
                  const optionKey = `${group.dimKey}:${item.id}`;
                  return (
                    <Checkbox
                      key={optionKey}
                      size="sm"
                      colorScheme="limeShade"
                      isChecked={selected.some((s) => s.value === optionKey)}
                      onChange={(e) => {
                        const option = { label: item.name, value: optionKey, dimKey: group.dimKey, itemId: item.id };
                        if (e.target.checked) {
                          onChange([...selected, option]);
                        } else {
                          onChange(selected.filter((s) => s.value !== optionKey));
                        }
                      }}
                      py={1.5}
                      w="100%"
                    >
                      <Text fontSize="13px" color="gray.600">
                        {item.name}
                      </Text>
                    </Checkbox>
                  );
                })}
              </React.Fragment>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

const ScopeFilterPill = ({ options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  const directScopes = options.filter((o) => o.value.startsWith("Scope"));
  const categories = options.filter((o) => o.value.startsWith("Category"));

  const selectedCatValues = selected.filter((s) => s.value.startsWith("Category"));
  const allCatsSelected = categories.length > 0 && categories.every((c) => selected.some((s) => s.value === c.value));
  const someCatsSelected = selectedCatValues.length > 0 && !allCatsSelected;

  const handleScope3Toggle = () => {
    if (allCatsSelected) {
      onChange(selected.filter((s) => !s.value.startsWith("Category")));
    } else {
      const currentNonCat = selected.filter((s) => !s.value.startsWith("Category"));
      onChange([...currentNonCat, ...categories]);
    }
  };

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        Scopes
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="240px"
          maxH="300px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {directScopes.map((option) => (
              <Checkbox
                key={option.value}
                size="sm"
                colorScheme="limeShade"
                isChecked={selected.some((s) => s.value === option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected, option]);
                  } else {
                    onChange(selected.filter((s) => s.value !== option.value));
                  }
                }}
                py={1.5}
                w="100%"
              >
                <Text fontSize="13px" color="gray.600">{option.label}</Text>
              </Checkbox>
            ))}

            {categories.length > 0 && (
              <>
                <Box w="100%" borderTop="1px solid" borderColor="gray.100" my={1} />
                <Checkbox
                  size="sm"
                  colorScheme="limeShade"
                  isChecked={allCatsSelected}
                  isIndeterminate={someCatsSelected}
                  onChange={handleScope3Toggle}
                  py={1.5}
                  w="100%"
                >
                  <Text fontSize="13px" fontWeight="600" color="gray.700">Scope 3</Text>
                </Checkbox>
                {categories.map((option) => (
                  <Checkbox
                    key={option.value}
                    size="sm"
                    colorScheme="limeShade"
                    isChecked={selected.some((s) => s.value === option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange([...selected, option]);
                      } else {
                        onChange(selected.filter((s) => s.value !== option.value));
                      }
                    }}
                    py={1.5}
                    w="100%"
                    pl={4}
                  >
                    <Text fontSize="13px" color="gray.600">{option.label}</Text>
                  </Checkbox>
                ))}
              </>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

const TaskFilterBar = ({
  filterObj,
  onServerFilterChange,
  localFilters,
  onLocalFilterChange,
  searchTerm,
  onSearchChange,
  statusOptions,
  quarterOptions,
  yearOptions,
  assigneeOptions,
  scopeOptions,
  dimensionGroups,
  showAssigneeFilter = true,
}) => {
  const serverFilterCount = Object.values(filterObj).filter(
    (v) => Array.isArray(v) && v.length > 0
  ).length;
  const localFilterCount = Object.values(localFilters).filter(
    (v) => Array.isArray(v) && v.length > 0
  ).length;
  const activeFilterCount = serverFilterCount + localFilterCount;

  const hasSearch = searchTerm?.length > 0;

  const handleClearAll = () => {
    onServerFilterChange("status", []);
    onServerFilterChange("quarter", []);
    onLocalFilterChange("assignee", []);
    onLocalFilterChange("scope", []);
    onLocalFilterChange("year", []);
    onLocalFilterChange("dimension", []);
    onSearchChange("");
  };

  return (
    <Flex align="center" gap={2} flexWrap="wrap">
      <InputGroup size="sm" maxW="220px">
        <InputLeftElement pointerEvents="none">
          <MagnifyingGlass size={14} color="#A0AEC0" />
        </InputLeftElement>
        <Input
          borderRadius="full"
          placeholder="Search tasks..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          borderColor={hasSearch ? "lime" : "gray.200"}
          bg={hasSearch ? "lightLime" : "white"}
          _hover={{ borderColor: "lime" }}
          _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
        />
      </InputGroup>


      <FilterPill
        label="Year"
        options={yearOptions}
        selected={localFilters.year || []}
        onChange={(val) => onLocalFilterChange("year", val)}
      />

      <FilterPill
        label="Quarter"
        options={quarterOptions}
        selected={filterObj.quarter || []}
        onChange={(val) => onServerFilterChange("quarter", val)}
      />

      <ScopeFilterPill
        options={scopeOptions}
        selected={localFilters.scope || []}
        onChange={(val) => onLocalFilterChange("scope", val)}
      />

      {dimensionGroups?.length > 0 && (
        <DimensionFilterPill
          dimensionGroups={dimensionGroups}
          selected={localFilters.dimension || []}
          onChange={(val) => onLocalFilterChange("dimension", val)}
        />
      )}

      <FilterPill
        label="Status"
        options={statusOptions}
        selected={filterObj.status || []}
        onChange={(val) => onServerFilterChange("status", val)}
      />

      {showAssigneeFilter && (
        <FilterPill
          label="Assignee"
          options={assigneeOptions}
          selected={localFilters.assignee || []}
          onChange={(val) => onLocalFilterChange("assignee", val)}
        />
      )}

      {(activeFilterCount > 0 || hasSearch) && (
        <Button
          size="sm"
          variant="ghost"
          color="gray.500"
          fontSize="12px"
          fontWeight="normal"
          leftIcon={<X size={12} />}
          onClick={handleClearAll}
          _hover={{ color: "gray.700" }}
        >
          Clear all
        </Button>
      )}
    </Flex>
  );
};

export default TaskFilterBar;
